// side effect: fetch new pvgis data when decisions are made
import {AttributeInterpreter, AttributeType, ChoiceValueDecisionState} from '@viamedici-spc/configurator-ts';
import {fetchPvgisHourly, initialCalculated, setSolarActivityLevel, setSolarAzimuth, setSolarCalculated, setSolarDimmProfile, setSolarVersion} from './solarSlice';
import {
	selectColourTemperature,
	selectSolarActivityLevel,
	selectSolarAzimuth,
	selectSolarAzimuthAngle,
	selectSolarDimmProfile, selectSolarGenerators,
	selectSolarLumPosition2IsVisible,
	selectSolarVersion
} from './solarSelectors';
import {selectLightDistribution, selectSpcSession} from '../selectors';
import {storeConfiguration} from '../slices/configurationSlice';
import {colourMapping, displayStyleMapping, regionMapping} from '../augmentProductData';
import {selectSolarMaxPower} from './solarSelectorsCalculations';
import {calculateLumFromPower} from '../util/calculateLumFromPower';

export const solarMiddleware = store => next => async action => {
	const state = store.getState();

	if (state.status.family !== 'Aeto') return next(action);

	//console.log('solarMiddleware action.type', action.type, action);

	// in front of others
	if (action.type === 'productData/productDataLoading/fulfilled') {
		action.payload.forEach(component => {

			component.features.forEach(feature => {

				// do not show some attributes
				switch (feature.featureId) {
					case 'LE_LuminairePositionHeight_1':
					case 'LE_LuminairePositionHeight_2':
					case 'LE_LuminairePositionAngle_1':
					case 'LE_LuminairePositionAngle_2':
						feature.isVisible = false;
						break;
					case 'LU_SolarUnitHeight':
						feature.displaySequenceUi = 105;
						break;
					case 'HS_HousingHeight':
						feature.displaySequenceUi = 106;
						break;
					case 'LE_LuminairePositionHeightSU_1':
						feature.displaySequenceUi = 107;
						break;
					case 'LE_LuminairePositionHeightSU_2':
						feature.displaySequenceUi = 108;
						feature.isVisible = true;
						break;
				}
			});
		});
	}

	// after others
	const result = next(action);

	if (action.type === 'solar/fetchSolarLocation/fulfilled' ||
		action.type === 'solar/setSolarUseCase' ||
		action.type === 'solar/setSolarShadowLevel' ||
		action.type === 'solar/setSolarActivityLevel' ||
		action.type === 'solar/setSolarAzimuth' ||
		action.type === 'solar/setSolarDimmProfile' ||
		action.type === 'solar/setSolarLuminarCount' ||
		action.type === 'solar/setSolarVersion' ||
		action.type === 'solar/setSolarTemperature' ||
		action.type === 'solar/setSolarBatteryType' ||
		action.type === 'solar/setSolarLumPosition1' ||
		action.type === 'solar/setSolarLumPosition2' ||
		action.type === 'solar/fetchPVGIS/fulfilled'
	) {
		if (store.getState().status.dataKey) {
			store.dispatch(storeConfiguration());
		}
	}

	if (action.type === 'spc/updateConfiguration') {
		const changes = action.payload.changes.attributes.changed;
		const spcConfiguration = store.getState().spc.spcs.default?.configuration;
		// solar version (Basic/Pro)
		const housingSizeChange = changes.find(c => c.key === 'root::HS_HousingSize');
		if (housingSizeChange) {
			const choice = AttributeInterpreter.getIncludedChoiceValues(housingSizeChange)[0];
			//console.log('housingSizeChanged', selectSolarVersion(state));
			if (choice.id === 'AetoBasic') {
				//console.log('solar version changed to basic', selectSolarDimmProfile(state));
				if (selectSolarDimmProfile(state) !== 'profile3' &&
					selectSolarDimmProfile(state) !== 'profile4' &&
					selectSolarDimmProfile(state) !== 'profile5') {
					/// setDimmProfile to profile4
					store.dispatch(setSolarDimmProfile({id: 'profile3'}));
				}
				if (selectSolarActivityLevel(state) !== 'none') {
					store.dispatch(setSolarActivityLevel({id: 'none'}));
				}
			}
		}
	}

	if (action.type === 'spc/updateConfiguration') {
		if (selectSolarGenerators(state) === null) return initialCalculated;

		const f3 = selectSolarMaxPower(state);
		const f3Rounded = {
			winter: Math.round(f3.winter * 100) / 100,
			spring: Math.round(f3.spring * 100) / 100,
			summer: Math.round(f3.summer * 100) / 100
		};
		const adjustedLum = {
			winter: Math.round(calculateLumFromPower(f3.winter, selectColourTemperature(state), selectLightDistribution(state))),
			spring: Math.round(calculateLumFromPower(f3.spring, selectColourTemperature(state), selectLightDistribution(state))),
			summer: Math.round(calculateLumFromPower(f3.summer, selectColourTemperature(state), selectLightDistribution(state)))
		};
		store.dispatch(setSolarCalculated(
			{
				solarCalculated: {
					summer: {
						power: f3Rounded.summer,
						lum: adjustedLum.summer
					},
					spring: {
						power: f3Rounded.spring,
						lum: adjustedLum.spring
					},
					winter: {
						power: f3Rounded.winter,
						lum: adjustedLum.winter
					}
				}
			})
		);
	}

	if (action.type === 'solar/setSolarActivityLevel') {
		const spcSession = selectSpcSession(state);

		if (action.payload.id === 'none') {
			spcSession.makeDecision(
				{
					type: AttributeType.Choice,
					attributeId: {localId: 'EB_MotionSensor_1'},
					choiceValueId: 'None',
					state: ChoiceValueDecisionState.Included
				}
			);
		} else {
			spcSession.makeDecision(
				{
					type: AttributeType.Choice,
					attributeId: {localId: 'EB_MotionSensor_1'},
					choiceValueId: 'Yes',
					state: ChoiceValueDecisionState.Included
				}
			);
		}
	}

	// enforce PRO and MotionSensor when needed
	if (action.type === 'solar/setSolarDimmProfile') {
		const spcSession = selectSpcSession(state);
		const profileId = action.payload.id;
		if (['profile0', 'profile1', 'profile2', 'profile6', 'profile7', 'profile8'].includes(profileId)) {
			spcSession.makeDecision(
				{
					type: AttributeType.Choice,
					attributeId: {localId: 'HS_HousingSize'},
					choiceValueId: 'AetoPro',
					state: ChoiceValueDecisionState.Included
				}
			);
			spcSession.makeDecision(
				{
					type: AttributeType.Choice,
					attributeId: {localId: 'EB_MotionSensor_1'},
					choiceValueId: 'Yes',
					state: ChoiceValueDecisionState.Included
				}
			);
			store.dispatch(setSolarActivityLevel({id: 'low'}));
			//store.dispatch(decision(
			//	{variableId: 'EB_MotionSensor_1', featureValueId: 'Yes', state: 'Included'}
			//));
		} else {
			spcSession.makeDecision(
				{
					type: AttributeType.Choice,
					attributeId: {localId: 'EB_MotionSensor_1'},
					choiceValueId: 'None',
					state: ChoiceValueDecisionState.Included
				}
			);
			store.dispatch(setSolarActivityLevel({id: 'none'}));

		}
	}


	if (action.type === 'solar/fetchSolarLocation/fulfilled') {
		const solar = store.getState().solar.data;
		await store.dispatch(setSolarAzimuth({id: solar.project.location.latitude > 0 ? 'n' : 's'}));
		// console.log('latitude', solar.project.location.latitude, solar.project.azimuth);
	}

	if (action.type === 'solar/setSolarAzimuth' || action.type === 'solar/fetchSolarLocation/fulfilled' || action.type === 'solar/setSolarData') {
		const solar = store.getState().solar.data;

		// get pvgis daa when not already available and location is set
		if ((!solar.project.orientation || solar.project.orientation.left.pvgisDaily === null) &&
			solar.project.azimuth !== null && solar.project.location !== null && solar.project.location.latitude !== null && solar.project.location.longitude !== null
		) {
			const angle = selectSolarAzimuthAngle(store.getState());
			//get the new pvgis data
			const options = {
				lat: solar.project.location.latitude,
				lon: solar.project.location.longitude,
				outputformat: 'json',
				usehorizon: '1',
				userhorizon: '0',
				startyear: '2013',
				endyear: '2023',
				pvcalculation: '1',
				pvtechchoice: 'crystSi',
				loss: '14',
				raddatabase: '',
				trackingtype: '0',
				optimalangles: '0',
				optimalinclination: '0',
				components: '1',
				peakpower: solar.project.panelPdc / 1000,
				mountingplace: 'free', // building / free
				aspect: 0, //azimuth selectSolarAzimuthAngle(store.getState()) ??
				angle: 90, //surface_tilt
			};
			// clone options with different aspect property (-180 - 180 degree)
			store.dispatch(fetchPvgisHourly({...options, aspect: ((angle + 0) + 360) % 360 - 180, orientation: 'front'}));
			store.dispatch(fetchPvgisHourly({...options, aspect: ((angle + 90) + 360) % 360 - 180, orientation: 'left'}));
			store.dispatch(fetchPvgisHourly({...options, aspect: ((angle + 180) + 360) % 360 - 180, orientation: 'back'}));
			store.dispatch(fetchPvgisHourly({...options, aspect: ((angle - 90) + 360) % 360 - 180, orientation: 'right'}));
		}
	}

	return result;
};
